import { useId, useMemo, useState } from "react";
import { Loader2, Undo2 } from "lucide-react";
import {
  FAMILY_ROLE_LABELS,
  IDENTITY_VERSION_RULE,
  PLACEMENT_RELATION_LABELS,
  PlacementRelationSchema,
  documentDisplayName,
  type DocumentFamily,
  type FamilyMember,
  type FamilyRole,
  type PlacementRelation,
  type ProjectDocument,
  type QuestionMember,
  type RelationToFile,
} from "@gb/contracts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useDocuments,
  usePlaceDocument,
  useUndoChange,
} from "@/features/project/documents-api";
import { cn } from "@/lib/utils";
import {
  formatDocumentDate,
  relationPhrase,
  revisionText,
  roleLead,
} from "./family-words";
import { FileLink, type CameFrom } from "./file-link";
import { PlacementQuestion } from "./placement-question";

/**
 * Where a file sits among its versions, and a person's way to change it
 * (`document-ai/0016`).
 *
 * It says the file's role, who placed it and why, and offers an Undo. When the
 * model was not sure, it asks the question here too (decided by the product
 * owner 2026-09-22, `document-ai/0016` §11). The review after an import asks the
 * same question with the same component (`placement-question.tsx`), because a
 * person who opens a file to look at it must be able to answer there as well.
 *
 * Every result is said inline, beside the button that caused it. There are no
 * toasts.
 */
export function DocumentVersion({
  projectId,
  document,
  from,
}: {
  projectId: string;
  document: ProjectDocument;
  /** Where the person opened this file from, carried on to the files it links. */
  from?: CameFrom;
}) {
  const ids = useId();
  const { family, question } = document;
  const name = document.title ?? documentDisplayName(document.filename);
  const [placing, setPlacing] = useState(false);
  const [confirmingUndo, setConfirmingUndo] = useState(false);
  // What the last action did, once it has done it. Cleared by the next one.
  const [done, setDone] = useState<string | null>(null);
  const answer = usePlaceDocument(projectId);
  const undo = useUndoChange(projectId);

  const saved = (): void => setDone("Saved. A later import won’t move it.");

  // Any member of the family's question can be answered here, not only this
  // file (VER-08, 2026-09-26): each answer places that member.
  const answerWith = (member: QuestionMember, yes: boolean): void => {
    setDone(null);
    undo.reset();
    answer.mutate(
      {
        documentId: member.document.id,
        placement: yes
          ? { relation: member.relation, documentId: member.other.id }
          : { relation: "on-its-own" },
      },
      { onSuccess: saved },
    );
  };

  const undoNow = (changeId: string): void => {
    setDone(null);
    answer.reset();
    undo.mutate(changeId, {
      onSuccess: () => setDone("Undone. A later import won’t move it."),
      onSettled: () => setConfirmingUndo(false),
    });
  };

  // An identical copy is the same bytes as its original, which nobody can be
  // wrong about, and it moves with its original. So it is never placed by
  // hand (`plans/project-files-build.md`, 3d: "Copies follow their original").
  const isCopy = family.role === "identical-copy";

  return (
    <>
      {question && (
        <div className="mt-3">
          <PlacementQuestion
            projectId={projectId}
            question={question}
            self={document.id}
            busy={answer.isPending}
            onAnswer={answerWith}
            from={from}
          />
        </div>
      )}
      {/* Outside the question, so it is still there when a refusal re-reads the
          file and the question has gone. */}
      {answer.error && (
        <p role="alert" className="mt-2 text-[0.85rem] font-semibold text-danger">
          {answer.error.message}
        </p>
      )}

      <section aria-labelledby={`${ids}-heading`} className="mt-3">
        <h2
          id={`${ids}-heading`}
          className="font-display text-[0.66rem] font-semibold tracking-[0.12em] text-muted uppercase"
        >
          Version
        </h2>

        <p className="mt-1 text-[0.88rem] text-ink">
          <RoleLine projectId={projectId} family={family} asked={question !== null} from={from} />
        </p>
        <p className="mt-0.5 text-[0.82rem] leading-[1.5] text-muted">{placedBy(family)}</p>
        {isCopy && (
          <p className="mt-0.5 text-[0.82rem] leading-[1.5] text-muted">
            It moves with its original.
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {!isCopy && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setDone(null);
                setPlacing(true);
              }}
            >
              Change
            </Button>
          )}
          {family.undoChangeId &&
            (confirmingUndo ? (
              // Inline, like Delete: a short question, lighter than a dialog.
              <span className="flex items-center gap-1.5">
                <span className="text-[0.78rem] font-semibold text-ink">Undo this placement?</span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={undo.isPending}
                  onClick={() => setConfirmingUndo(false)}
                >
                  No
                </Button>
                <Button
                  size="sm"
                  className="hover:bg-brand-deep"
                  disabled={undo.isPending}
                  onClick={() => undoNow(family.undoChangeId!)}
                >
                  Yes
                </Button>
              </span>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setDone(null);
                  setConfirmingUndo(true);
                }}
              >
                <Undo2 size={15} />
                Undo
              </Button>
            ))}
        </div>

        {undo.error && (
          <p role="alert" className="mt-2 text-[0.85rem] font-semibold text-danger">
            {undo.error.message}
          </p>
        )}
        {done && (
          <p role="status" className="mt-2 text-[0.85rem] font-semibold text-ok">
            {done}
          </p>
        )}

        {family.members.length > 0 && (
          <div className="mt-3">
            <p id={`${ids}-members`} className="text-[0.78rem] font-semibold text-ink-slate">
              {family.name ? `Other files in “${family.name}”` : "Related files"}
            </p>
            <ul aria-labelledby={`${ids}-members`} className="mt-1 flex flex-col gap-1">
              {family.members.map((member) => (
                <li key={member.id} className="text-[0.82rem] text-muted">
                  <FileLink projectId={projectId} id={member.id} from={from}>
                    {member.name}
                  </FileLink>
                  {" · "}
                  {memberRole(member, document.id, family.members)}
                  {member.documentDate && <> · Dated {formatDocumentDate(member.documentDate)}</>}
                  {member.revision && <> · {revisionText(member.revision)}</>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {placing && (
        <PlaceDocumentDialog
          projectId={projectId}
          document={document}
          name={name}
          onClose={() => setPlacing(false)}
          onSaved={saved}
        />
      )}
    </>
  );
}

/**
 * The role in words. A role that answers is its label. One that defers says
 * what to: "Superseded by “Ramming plan rev 02”", with the name a link.
 *
 * A file with no relatives says so, once placement has looked at it. Not while
 * a question is open: the question is whether it has one.
 */
function RoleLine({
  projectId,
  family,
  asked,
  from,
}: {
  projectId: string;
  family: DocumentFamily;
  asked: boolean;
  from?: CameFrom;
}) {
  const lead = roleLead(family.role);
  if (lead && family.related) {
    return (
      <>
        <span className="font-semibold">{lead}</span> “
        <FileLink projectId={projectId} id={family.related.id} from={from}>
          {family.related.name}
        </FileLink>
        ”
      </>
    );
  }
  if (family.id === null && family.role === "current" && family.madeBy !== null && !asked) {
    return <span className="font-semibold">{PLACEMENT_RELATION_LABELS["on-its-own"]}</span>;
  }
  return <span className="font-semibold">{FAMILY_ROLE_LABELS[family.role]}</span>;
}

/** Who placed the file, and their one line on why. */
function placedBy(family: DocumentFamily): string {
  const reason = family.reason ? ` — ${family.reason}` : "";
  switch (family.madeBy) {
    case "model":
      return `Placed by the model${reason}`;
    case "person":
      return `Placed by a person${reason}`;
    case "code":
      return `Found by code${reason}`;
    default:
      // `placed_at` null: placement has not looked at the file yet.
      return "Not checked for other versions yet.";
  }
}

/** Another file's role, and what it defers to when that is on the page. */
function memberRole(
  member: FamilyMember,
  documentId: string,
  members: readonly FamilyMember[],
): string {
  const lead = roleLead(member.role);
  if (!lead || !member.relatedDocumentId) return FAMILY_ROLE_LABELS[member.role];
  if (member.relatedDocumentId === documentId) return `${lead} this file`;
  const target = members.find((other) => other.id === member.relatedDocumentId);
  return target ? `${lead} “${target.name}”` : FAMILY_ROLE_LABELS[member.role];
}

/**
 * The relation that says what a file already is, from its own side, so the
 * dialog opens on the current placement. A current file in a family has no
 * single relation from its side, so the dialog opens with nothing chosen.
 */
const RELATION_FOR_ROLE: Partial<Record<FamilyRole, RelationToFile>> = {
  superseded: "replaced-by",
  "alternative-format": "other-format-of",
  "signature-state": "earlier-signature-of",
};

function initialPlacement(family: DocumentFamily): {
  relation: PlacementRelation | "";
  documentId: string;
} {
  if (family.id === null) return { relation: "on-its-own", documentId: "" };
  const relation = RELATION_FOR_ROLE[family.role];
  if (relation && family.related && offered(relation, family)) {
    return { relation, documentId: family.related.id };
  }
  return { relation: "", documentId: "" };
}

/**
 * The relations that make one file a newer or older version of another. An
 * email is one message and a photograph is one capture: another file is never
 * the same document in a newer state, so neither is offered (`document-ai/0016`
 * §13 and §14, 2026-09-25). The server refuses both anyway; the dialog does not
 * offer a choice it would refuse.
 */
const VERSION_RELATIONS: ReadonlySet<PlacementRelation> = new Set(["replaces", "replaced-by"]);

function offered(relation: PlacementRelation, family: Pick<DocumentFamily, "identity">): boolean {
  return family.identity === null || !VERSION_RELATIONS.has(relation);
}

/** How another file is named in the list: its name, then what tells versions apart. */
function candidateLabel(candidate: ProjectDocument): string {
  return [
    candidate.title ?? documentDisplayName(candidate.filename),
    candidate.revision ? revisionText(candidate.revision) : null,
    candidate.documentDate ? formatDocumentDate(candidate.documentDate) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

const SELECT_CLASS =
  "h-11 w-full rounded-input border-[1.5px] border-border bg-surface px-3 text-[0.9rem] text-ink disabled:opacity-50";

/**
 * Place a file by hand: what it is, and to which other file. Exported for its
 * test.
 *
 * Native selects, like the journal composer's: a list of every file in the
 * project is long, and the browser's own list scrolls and types-to-find.
 */
export function PlaceDocumentDialog({
  projectId,
  document,
  name,
  onClose,
  onSaved,
}: {
  projectId: string;
  document: Pick<ProjectDocument, "id" | "family">;
  name: string;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const ids = useId();
  const start = initialPlacement(document.family);
  const [relation, setRelation] = useState<PlacementRelation | "">(start.relation);
  const [otherId, setOtherId] = useState(start.documentId);
  const save = usePlaceDocument(projectId);
  const { data: documents, isPending: listing } = useDocuments(projectId);

  // Every other file, but not an identical copy: a copy moves with its
  // original, so the original is the one to name. For a newer or older
  // version, not an email or a photograph either.
  const candidates = useMemo(
    () =>
      (documents ?? [])
        .filter(
          (candidate) =>
            candidate.id !== document.id &&
            candidate.family.role !== "identical-copy" &&
            (relation === "" || offered(relation, candidate.family)),
        )
        .map((candidate) => ({ id: candidate.id, label: candidateLabel(candidate) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [documents, document.id, relation],
  );
  const relations = PlacementRelationSchema.options.filter((option) =>
    offered(option, document.family),
  );
  const identity = document.family.identity;

  const needsFile = relation !== "" && relation !== "on-its-own";
  const other = candidates.find((candidate) => candidate.id === otherId);
  const ready = relation === "on-its-own" || (needsFile && other !== undefined);

  // Said beside the disabled button, so it is not a mystery why it will not
  // press, and read back once it will: what is about to be saved.
  const hint =
    relation === ""
      ? "Choose what this file is."
      : relation === "on-its-own"
        ? `This file is ${relationPhrase(relation)}.`
        : other
          ? `This file is ${relationPhrase(relation)} “${other.label}”.`
          : "Choose the other file.";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>Version</DialogTitle>
            <DialogDescription className="mt-1">
              Say how “{name}” relates to the project’s other files. A later import won’t
              move it.
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-[7px]">
            <label
              htmlFor={`${ids}-relation`}
              className="font-display text-[0.72rem] font-semibold tracking-[0.09em] text-ink-slate uppercase"
            >
              This file is…
            </label>
            <select
              id={`${ids}-relation`}
              className={SELECT_CLASS}
              value={relation}
              disabled={save.isPending}
              onChange={(event) =>
                setRelation(PlacementRelationSchema.parse(event.target.value))
              }
            >
              {relation === "" && (
                <option value="" disabled>
                  Choose…
                </option>
              )}
              {relations.map((option) => (
                <option key={option} value={option}>
                  {PLACEMENT_RELATION_LABELS[option]}
                </option>
              ))}
            </select>
            {identity && (
              <p className="text-[0.82rem] leading-[1.5] text-muted">
                {IDENTITY_VERSION_RULE[identity]}
              </p>
            )}
          </div>

          {needsFile && (
            <div className="mt-4 flex flex-col gap-[7px]">
              <label
                htmlFor={`${ids}-other`}
                className="font-display text-[0.72rem] font-semibold tracking-[0.09em] text-ink-slate uppercase"
              >
                The other file
              </label>
              {candidates.length === 0 ? (
                <p className="text-[0.85rem] text-muted">
                  {listing ? "Loading the project’s files…" : "This project has no other files."}
                </p>
              ) : (
                <select
                  id={`${ids}-other`}
                  className={SELECT_CLASS}
                  value={other ? otherId : ""}
                  disabled={save.isPending}
                  onChange={(event) => setOtherId(event.target.value)}
                >
                  {!other && (
                    <option value="" disabled>
                      Choose a file…
                    </option>
                  )}
                  {candidates.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <p
            id={`${ids}-hint`}
            className={cn("mt-3 text-[0.82rem]", ready ? "text-muted" : "font-semibold text-warn-text")}
          >
            {hint}
          </p>
          {save.error && (
            <p role="alert" className="mt-2 text-[0.85rem] font-semibold text-danger">
              {save.error.message}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            className="hover:bg-brand-deep"
            disabled={!ready || save.isPending}
            aria-describedby={`${ids}-hint`}
            onClick={() => {
              if (relation === "") return;
              save.mutate(
                {
                  documentId: document.id,
                  placement:
                    relation === "on-its-own"
                      ? { relation }
                      : { relation, documentId: otherId },
                },
                {
                  onSuccess: () => {
                    onSaved?.();
                    onClose();
                  },
                },
              );
            }}
          >
            {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
